import { useSelector } from "react-redux";
import {
  clearLocalChanges,
  selectLocalChanges,
} from "../../app/slices/WebSocketSlice";
import PiModal, { PiModalProps } from "../../components/PiModal";
import { useMemo } from "react";
import WarningLabel from "./WarningLabel";
import PiButton from "../../components/PiButton";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import useSaveLocalChanges from "./SaveLocalChanges.hook";
import { useDispatch } from "react-redux";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { useSaveTraining } from "../../hooks/SaveTrainingHook";

export default function LocalChangesModal(props: PiModalProps) {
  const localChanges = useSelector(selectLocalChanges);
  const dispatch = useDispatch();
  const { saveLocalChanges, working, itemStatuses } = useSaveLocalChanges();
  const { saveTraining } = useSaveTraining();

  const envChanges = useMemo(() => {
    const env = Object.values(localChanges).filter(
      (change) => change.type === "Environment"
    );
    if (env.length === 0) {
      return <></>;
    }
    const changes = env.map((change) => (
      <div
        key={change.id}
        className="w-full h-[24px] flex items-center gap-[8px] text-creator-text"
      >
        <BusinessOutlinedIcon color="inherit" />
        <div className="text-[14px] font-medium text-creator-text">
          {change.name}
        </div>
        <div className="text-[12px] text-creator-text">
          {itemStatuses.get(change.id)?.status !== "pending"
            ? itemStatuses.get(change.id)?.status
            : ""}
        </div>
      </div>
    ));
    return (
      <div className="w-full gap-y-[12px] flex flex-col">
        <div className="text-[18px] font-bold">Environments</div>
        <div>{changes}</div>
      </div>
    );
  }, [localChanges, itemStatuses]);

  const propChanges = useMemo(() => {
    const props = Object.values(localChanges).filter(
      (change) => change.type === "Prop"
    );
    if (props.length === 0) {
      return <></>;
    }
    const changes = props.map((change) => (
      <div
        key={change.id}
        className="w-full h-[24px] flex items-center gap-[8px] text-creator-text"
      >
        <ViewInArOutlinedIcon color="inherit" />
        <div className="text-[14px] font-medium text-creator-text">
          {change.name}
        </div>
        <div className="text-[12px] text-creator-text">
          {itemStatuses.get(change.id)?.status !== "pending"
            ? itemStatuses.get(change.id)?.status
            : ""}
        </div>
      </div>
    ));
    return (
      <div className="w-full gap-y-[12px] flex flex-col">
        <div className="text-[18px] font-bold">Props</div>
        <div>{changes}</div>
      </div>
    );
  }, [localChanges, itemStatuses]);

  const environmentPropChanges = useMemo(() => {
    const environmentProps = Object.values(localChanges).filter(
      (change) => change.type === "EnvironmentProps"
    );

    if (environmentProps.length === 0) return <></>;

    const changes = environmentProps.map((change) => (
      <div
        key={change.id}
        className="w-full h-[24px] flex items-center gap-[8px] text-creator-text"
      >
        <ViewInArOutlinedIcon color="inherit" />
        <div className="text-[14px] font-medium text-creator-text">
          {change.name}
        </div>
        <div className="text-[12px] text-creator-text">
          {itemStatuses.get(change.id)?.status !== "pending"
            ? itemStatuses.get(change.id)?.status
            : ""}
        </div>
      </div>
    ));

    return (
      <div className="w-full gap-y-[12px] flex flex-col">
        <div className="text-[18px] font-bold">Environment Props</div>
        <div>{changes}</div>
      </div>
    );
  }, [localChanges, itemStatuses]);

  const situationChanges = useMemo(() => {
    const situations = Object.values(localChanges).filter(
      (change) => change.type === "Situation"
    );

    if (situations.length === 0) return <></>;

    const changes = situations.map((change) => (
      <div
        key={change.id}
        className="w-full h-[24px] flex items-center gap-[8px] text-creator-text"
      >
        <ViewInArOutlinedIcon color="inherit" />
        <div className="text-[14px] font-medium text-creator-text">
          {change.name}
        </div>
        <div className="text-[12px] text-creator-text">
          {itemStatuses.get(change.id)?.status !== "pending"
            ? itemStatuses.get(change.id)?.status
            : ""}
        </div>
      </div>
    ));
    return (
      <div className="w-full gap-y-[12px] flex flex-col">
        <div className="text-[18px] font-bold">Situations</div>
        <div>{changes}</div>
      </div>
    );
  }, [localChanges, itemStatuses]);

  const publishAllButton = (
    <div className="h-[36px] w-[256px]">
      <PiButton
        variant="piBlue"
        onClick={async () => {
          const publishedSuccessfully = await saveLocalChanges();
          if (publishedSuccessfully) {
            await saveTraining();
          }
        }}
        disabled={working}
      >
        Publish and Save
      </PiButton>
    </div>
  );

  const finishButton = (
    <div className="h-[36px] w-[256px]">
      <PiButton
        variant="piBlue"
        onClick={() => {
          dispatch(clearLocalChanges());
          props.onClose();
        }}
        disabled={false}
      >
        Finish
      </PiButton>
    </div>
  );

  const allItemsSaved = useMemo(() => {
    //console.log(itemStatuses);
    return Array.from(itemStatuses.values()).every(
      (item) => item.status === "success" || item.status === "error"
    );
  }, [itemStatuses]);

  return (
    <PiModal {...props}>
      <div className="flex flex-col gap-y-[12px] w-full h-full">
        <WarningLabel text="These local changes must be saved before you can save the project" />
        <div className="flex-1 w-full h-full flex items-center gap-[12px] flex-col">
          {envChanges}
          {environmentPropChanges}
          {propChanges}
          {situationChanges}
        </div>
        {!allItemsSaved && publishAllButton}
        {allItemsSaved && finishButton}
      </div>
    </PiModal>
  );
}
